import React, { useState } from 'react';
import { COLORS, toDateInput, toTimeInput, fmtDate, fmtTime } from './calendarUtils';

const REMINDERS = [
  { value: 0,     label: 'No reminder' },
  { value: 5,     label: '5 minutes before' },
  { value: 15,    label: '15 minutes before' },
  { value: 30,    label: '30 minutes before' },
  { value: 60,    label: '1 hour before' },
  { value: 120,   label: '2 hours before' },
  { value: 1440,  label: '1 day before' },
  { value: 10080, label: '1 week before' },
];

// calendars: [{ id, name, color }] the user can add to; calendar 'personal' = own appointment.
// readOnly: show the details without editing (users below foreman, or holidays/birthdays).
export default function EventForm({ event, defaultStart, calendars, defaultCalendar, readOnly, onSave, onDelete, onClose }) {
  const isNew = !event?._id;
  const isOccurrence = !!event?.isOccurrence;

  // Editing an occurrence edits the whole series — start from the series' first date
  const evStart = event ? new Date(isOccurrence ? event.seriesStart : event.start) : null;
  const evEnd   = event ? new Date(evStart.getTime() + (new Date(event.end) - new Date(event.start))) : null;
  const initStart = evStart || defaultStart || new Date();
  const initEnd   = evEnd   || new Date(initStart.getTime() + 60 * 60 * 1000);

  const [calendar,    setCalendar]    = useState(event ? (event.project || 'personal') : defaultCalendar || calendars[0]?.id || 'personal');
  const [title,       setTitle]       = useState(event?.title || '');
  const [location,    setLocation]    = useState(event?.location || '');
  const [description, setDescription] = useState(event?.description || '');
  const [allDay,      setAllDay]      = useState(event?.allDay || false);
  const [startDate,   setStartDate]   = useState(toDateInput(initStart));
  const [startTime,   setStartTime]   = useState(toTimeInput(initStart));
  const [endDate,     setEndDate]     = useState(toDateInput(initEnd));
  const [endTime,     setEndTime]     = useState(toTimeInput(initEnd));
  const [color,       setColor]       = useState(event?.color || '');
  const [reminder,    setReminder]    = useState(event?.reminder ?? 0);
  const [recurring,   setRecurring]   = useState(!!event?.recurrence);
  const [recType,     setRecType]     = useState(event?.recurrence?.type || 'weekly');
  const [recInterval, setRecInterval] = useState(event?.recurrence?.interval || 1);
  const [recEndType,  setRecEndType]  = useState(event?.recurrence?.endType || 'never');
  const [recEndDate,  setRecEndDate]  = useState(event?.recurrence?.endDate ? toDateInput(new Date(event.recurrence.endDate)) : '');
  const [recCount,    setRecCount]    = useState(event?.recurrence?.count || 10);
  const [error,       setError]       = useState('');

  const calColor = calendars.find(c => c.id === calendar)?.color || COLORS[0];

  // Keep the end after the start when the start moves
  const moveStart = (date, time) => {
    const oldS = new Date(`${startDate}T${startTime || '00:00'}`);
    const oldE = new Date(`${endDate}T${endTime || '00:00'}`);
    const newS = new Date(`${date}T${time || '00:00'}`);
    setStartDate(date); setStartTime(time);
    if (!isNaN(newS) && !isNaN(oldS) && !isNaN(oldE)) {
      const e = new Date(newS.getTime() + Math.max(0, oldE - oldS));
      setEndDate(toDateInput(e)); setEndTime(toTimeInput(e));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const start = new Date(`${startDate}T${allDay ? '00:00:00' : startTime}`);
    const end   = new Date(`${endDate}T${allDay ? '23:59:59' : endTime}`);
    if (isNaN(start) || isNaN(end)) { setError('Enter a valid start and end.'); return; }
    if (end < start) { setError('The end is before the start.'); return; }
    if (recurring && recEndType === 'date' && !recEndDate) { setError('Pick the date the repeat ends.'); return; }
    onSave({
      project: calendar === 'personal' ? null : calendar,
      title: title.trim(),
      location: location.trim(),
      description: description.trim(),
      start, end, allDay, color, reminder,
      recurrence: recurring ? {
        type: recType,
        interval: Number(recInterval) || 1,
        endType: recEndType,
        endDate: recEndType === 'date'  ? new Date(`${recEndDate}T23:59:59`) : null,
        count:   recEndType === 'count' ? Number(recCount) || 1 : null,
      } : null,
    }, event);
  };

  if (readOnly) {
    const s = new Date(event.start), en = new Date(event.end);
    const when = event.allDay
      ? (fmtDate(s) === fmtDate(en) ? fmtDate(s) : `${fmtDate(s)} – ${fmtDate(en)}`)
      : `${fmtDate(s)} ${fmtTime(s)} – ${fmtDate(s) === fmtDate(en) ? '' : fmtDate(en) + ' '}${fmtTime(en)}`;
    return (
      <div className="cal-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="cal-modal">
          <h3>{event.title}</h3>
          <div className="cal-view-row">{when}</div>
          {event.calendarName && <div className="cal-view-row cal-view-muted">{event.calendarName}</div>}
          {event.location && <div className="cal-view-row">📍 {event.location}</div>}
          {event.description && <div className="cal-view-row cal-view-desc">{event.description}</div>}
          <div className="cal-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cal-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cal-modal">
        <h3>{isNew ? 'New event' : 'Edit event'}</h3>
        {isOccurrence && <p className="cal-form-note">Changes apply to every repeat of this event.</p>}
        <form onSubmit={handleSubmit}>
          {calendars.length > 1 && (
            <div className="cal-form-row">
              <label>Calendar</label>
              <select value={calendar} onChange={e => setCalendar(e.target.value)} disabled={!isNew}>
                {calendars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          <div className="cal-form-row">
            <label>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} autoFocus required placeholder="e.g. Building meeting" />
          </div>

          <div className="cal-form-row">
            <label>Location</label>
            <input value={location} onChange={e => setLocation(e.target.value)} />
          </div>

          <div className="cal-form-row">
            <label>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="cal-form-row">
            <div className="cal-form-inline">
              <input type="checkbox" id="cal-allday" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
              <label htmlFor="cal-allday">All day</label>
            </div>
          </div>

          <div className="cal-form-pair">
            <div className="cal-form-row">
              <label>Start</label>
              <div className="cal-dt">
                <input type="date" value={startDate} onChange={e => moveStart(e.target.value, startTime)} required />
                {!allDay && <input type="time" value={startTime} onChange={e => moveStart(startDate, e.target.value)} required />}
              </div>
            </div>
            <div className="cal-form-row">
              <label>End</label>
              <div className="cal-dt">
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />
                {!allDay && <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} required />}
              </div>
            </div>
          </div>

          <div className="cal-form-row">
            <label>Reminder</label>
            <select value={reminder} onChange={e => setReminder(Number(e.target.value))}>
              {REMINDERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {reminder > 0 && (
              <span className="cal-form-hint">
                {calendar === 'personal'
                  ? 'Sent as a push notification to you.'
                  : 'Sent as a push notification to everyone who shows this project calendar in My Calendar.'}
              </span>
            )}
          </div>

          <div className="cal-form-row">
            <label>Colour</label>
            <div className="cal-color-row">
              <div className={`cal-color-swatch cal-color-default${!color ? ' selected' : ''}`}
                style={{ background: calColor }} title="Calendar colour" onClick={() => setColor('')} />
              {COLORS.filter(c => c !== calColor).map(c => (
                <div key={c} className={`cal-color-swatch${color === c ? ' selected' : ''}`}
                  style={{ background: c }} onClick={() => setColor(c)} />
              ))}
            </div>
          </div>

          <div className="cal-form-row">
            <div className="cal-form-inline">
              <input type="checkbox" id="cal-recurring" checked={recurring} onChange={e => setRecurring(e.target.checked)} />
              <label htmlFor="cal-recurring">Repeat</label>
            </div>
          </div>

          {recurring && (
            <div className="cal-recur-section">
              <div className="cal-recur-row">
                <label>Every</label>
                <input type="number" min="1" max="99" style={{ width: 50 }} value={recInterval} onChange={e => setRecInterval(e.target.value)} />
                <select value={recType} onChange={e => setRecType(e.target.value)}>
                  <option value="daily">day(s)</option>
                  <option value="weekly">week(s)</option>
                  <option value="monthly">month(s)</option>
                  <option value="yearly">year(s)</option>
                </select>
              </div>
              <div className="cal-recur-row">
                <label>Ends</label>
                <select value={recEndType} onChange={e => setRecEndType(e.target.value)}>
                  <option value="never">Never</option>
                  <option value="date">On date</option>
                  <option value="count">After</option>
                </select>
                {recEndType === 'date' && <input type="date" value={recEndDate} onChange={e => setRecEndDate(e.target.value)} />}
                {recEndType === 'count' && <>
                  <input type="number" min="1" max="999" style={{ width: 60 }} value={recCount} onChange={e => setRecCount(e.target.value)} />
                  <label>times</label>
                </>}
              </div>
            </div>
          )}

          {error && <div className="cal-form-error">{error}</div>}

          <div className="cal-modal-actions">
            {!isNew && onDelete && <button type="button" className="btn-danger" onClick={onDelete}>Delete</button>}
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
